/**
 * アバター写真を入力するコンポーネント
 */

import AvatarDefaultIcon from '@assets/images/avatar/default.png';
import AvatarEditModal from '@src/ui/elements/avatar/AvatarEditModal';
import ErrorMessage from '@src/ui/elements/form/ErrorMessage';
import { ERROR_MESSAGES } from '@src/utils/errorMessages';
import logger from '@src/utils/logger';
import { useEffect, useState } from 'react';
import { Area } from 'react-easy-crop';

type Props = {
  onImageChange: (file: File | null) => void;
  url?: string;
  error?: string | null;
  responsive?: boolean;
};

const AvatarInput = ({ onImageChange, url, error, responsive = true }: Props) => {
  const [image, setImage] = useState<string>(AvatarDefaultIcon); // 表示している画像
  const [showModal, setShowModal] = useState(false); // モーダルの表示/非表示
  const [selectedFile, setSelectedFile] = useState<File | null>(null); // 選択されたファイルオブジェクト
  const [previewImage, setPreviewImage] = useState<string>(AvatarDefaultIcon); // トリミング前の元画像
  const [tempImage, setTempImage] = useState<string>(''); // トリミング作業用の一時的な画像

  useEffect(() => {
    setImage(url ? url : AvatarDefaultIcon);
  }, [url]);

  // ファイル選択時
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      const maxFileSize = 10 * 1024 * 1024;
      if (file.size > maxFileSize) {
        alert(ERROR_MESSAGES.FILE.MAX_AVATAR_SIZE_EXCEEDED(maxFileSize / (1024 * 1024)));
        e.target.value = '';
        return;
      }

      const validExtensions = ['jpeg', 'jpg', 'png', 'gif'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!validExtensions.includes(fileExtension || '')) {
        alert(ERROR_MESSAGES.FILE.INVALID_FORMAT);
        e.target.value = '';
        return;
      }

      setSelectedFile(file); // 選択されたファイルを保存

      // FileReaderを使用してファイルをData URL形式に変換
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        setTempImage(result); // トリミング用の一時画像として保存
        setShowModal(true); // トリミング用のモーダルを表示
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // トリミング後の画像生成
  const createCroppedImage = async (croppedAreaPixels: Area) => {
    if (!selectedFile) {
      logger.error('selectedFile is null');
      return;
    }

    // Canvas要素を作成してトリミング処理を行う
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.imageSmoothingQuality = 'high';
    }

    const img = new Image();
    return new Promise<void>((resolve) => {
      img.onload = () => {
        const resizeWidth = 300;
        const resizeHeight = 300;
        // トリミングサイズに合わせてCanvasのサイズを設定
        canvas.width = resizeWidth;
        canvas.height = resizeHeight;

        ctx?.clearRect(0, 0, resizeWidth, resizeHeight);

        // 画像をトリミング
        ctx?.drawImage(
          img,
          croppedAreaPixels.x, // トリミング開始位置X
          croppedAreaPixels.y, // トリミング開始位置Y
          croppedAreaPixels.width, // トリミング幅
          croppedAreaPixels.height, // トリミング高さ
          0, // 描画開始位置X
          0, // 描画開始位置Y
          resizeWidth, // 描画幅
          resizeHeight, // 描画高さ
        );

        const fileType = selectedFile.type;

        // トリミングした画像をData URLとして保存
        const previewUrl = canvas.toDataURL(fileType);
        setPreviewImage(previewUrl); // プレビュー用に保存
        setImage(previewUrl); // 表示用に保存
        // トリミングした画像をBlobに変換してFileオブジェクトを作成
        canvas.toBlob((blob) => {
          if (!blob) {
            logger.error('Blob is null');
            return;
          }

          const croppedFile = new File([blob], selectedFile.name, {
            type: fileType,
            lastModified: new Date().getTime(),
          });
          onImageChange(croppedFile); // 親コンポーネントに渡す
          setShowModal(false); // モーダルを閉じる
          resolve();
        }, fileType);
      };
      img.src = tempImage; // トリミング用の一時画像を読み込み
    });
  };

  // 画像削除時
  const handleDelete = () => {
    setImage(AvatarDefaultIcon); // 表示をデフォルトに戻す
    setPreviewImage(AvatarDefaultIcon); // プレビューもデフォルトに戻す
    onImageChange(null); // 親コンポーネントに通知
    setSelectedFile(null); // 選択中のファイルをクリア
  };

  // トリミングキャンセル時の処理
  const handleCancel = () => {
    setShowModal(false);
    if (!selectedFile) {
      setImage(previewImage); // トリミング前の状態に戻す
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      <div
        className={`flex flex-col gap-[10px]
          ${responsive ? 'lg:flex-row lg:gap-4' : ''}
        `}
      >
        <div
          className={`size-[100px] overflow-hidden rounded-full
            ${error ? ' border-2 border-solid border-warning' : ' '}`}
        >
          <img
            src={image}
            alt='avatar_img'
            width={100}
            height={100}
            loading='lazy'
            className='bg-white object-cover'
          />
        </div>
        <div
          className={`flex justify-center gap-2
            ${responsive ? 'lg:items-center' : ''}
          `}
        >
          <label className='cursor-pointer text-xs text-link underline hover:no-underline'>
            変更する
            <input
              type='file'
              accept='image/jpeg, image/jpg, image/png, image/gif'
              className='hidden'
              onChange={handleFileChange}
            />
          </label>
          {image !== AvatarDefaultIcon && (
            <button
              className='text-xs text-link underline hover:no-underline'
              onClick={handleDelete}
              type='button'
            >
              削除する
            </button>
          )}
        </div>
      </div>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {showModal && (
        <AvatarEditModal
          imageUrl={tempImage}
          onClose={handleCancel}
          onSave={createCroppedImage}
        />
      )}
    </div>
  );
};

export default AvatarInput;
